import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, FileText, ArrowRight, Target } from 'lucide-react';

const MilitaryInstallations = () => {
  const serviceAreas = [
    {
      title: 'Comprehensive Air Quality Assessments',
      description: 'Thorough evaluations of air quality at military installations to identify potential contaminants and areas for improvement.',
    },
    {
      title: 'Regulatory Compliance',
      description: 'Expert guidance on federal and state air quality regulations, ensuring installations meet all environmental requirements.',
    },
    {
      title: 'Customized Solutions',
      description: 'Tailored strategies addressing unique challenges faced by military facilities, from remote locations to specialized operations.',
    },
    {
      title: 'Advanced Monitoring Systems',
      description: 'Implementation of state-of-the-art monitoring technologies to track air quality continuously and accurately.',
    },
  ];

  const benefits = [
    'Enhanced health and well-being for service members',
    'Improved operational efficiency and performance',
    'Reduced environmental impact',
    'Compliance with all applicable regulations',
    'Long-term cost savings through efficient systems',
    'Support for mission readiness',
  ];

  return (
    <div className="min-h-screen" data-testid="military-page">
      {/* Hero Section */}
      <section
        className="hero-section relative text-white overflow-hidden"
        style={{
          height: '60vh',
          maxHeight: '720px',
          minHeight: '450px',
          padding: '5rem 0'
        }}
      >
        {/* Responsive Hero Image Container */}
        <div className="absolute inset-0 z-0">
          <style>{`
            .hero-section .hero-image,
            .hero-section .hero-image img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              display: block;
            }
            
            @media (max-width: 900px) {
              .hero-section {
                height: auto !important;
                max-height: none !important;
                padding: 3rem 0 !important;
              }
            }
          `}</style>
          
          <picture className="hero-image">
            <source
              media="(min-width: 1280px)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/m5y2d6ps_military_hero_desktop_2560x1200.webp"
              type="image/webp" />
            <source
              media="(min-width: 900px)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/ffa84j0m_military_ops_tablet_1536x900.webp"
              type="image/webp" />
            <source
              media="(orientation: landscape)"
              srcSet="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/94x2pv64_military_ops_mobile_horizontal_960x640.webp"
              type="image/webp" />
            <img
              src="https://customer-assets.emergentagent.com/job_eco-ui-update/artifacts/3na8igmw_military_ops_mobile_vertical_960x1200.webp"
              alt="Military operations with transport trucks and personnel near a power plant facility"
              loading="eager"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </picture>
          
          {/* Dark Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 via-gray-900/50 to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 h-full flex items-center">
          <div className="max-w-3xl">
            <div className="flex items-center mb-6">
              <Shield className="w-16 h-16 mr-4" />
              <div>
                <h1 className="text-4xl md:text-5xl font-bold">
                  Military Installations
                </h1>
                <p className="text-xl mt-2">Air Quality Control & Environmental Compliance</p>
              </div>
            </div>
            <p className="text-xl md:text-2xl max-w-3xl">
              Ensuring armed forces operate in environments that prioritize health, performance, and regulatory compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Supporting Our Armed Forces</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Raven Environmental Consulting, LLC specializes in air quality control for military installations, ensuring that our armed forces operate in environments that prioritize health and performance. With a commitment to precision and effectiveness, we offer tailored solutions that address unique challenges faced by military facilities.
            </p>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Military installations in Alaska face distinctive environmental challenges due to remote locations, extreme weather conditions, and diverse operational requirements. Our team understands these unique factors and provides specialized expertise to maintain superior air quality while supporting mission readiness.
            </p>
            
            <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-700">
              <h3 className="font-bold text-gray-900 mb-3">Mission-Critical Support</h3>
              <p className="text-gray-700">
                We are dedicated to supporting our servicemen and women with the best air quality solutions available, fostering a safer, healthier, and more productive environment at military installations throughout Alaska.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Specialized Services for Military Facilities</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {serviceAreas.map((area, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-lg border-t-4 border-green-700">
                <div className="flex items-start mb-4">
                  <Target className="w-8 h-8 text-green-700 mr-3 flex-shrink-0" />
                  <h3 className="text-2xl font-bold text-gray-900">{area.title}</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regulatory Compliance */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Federal Regulatory Requirements</h2>
          <div className="space-y-8 max-w-5xl mx-auto">
            {/* NSPS */}
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 60 - New Source Performance Standards</h3>
              <p className="text-gray-600 mb-4">Military installations must comply with NSPS for various emission sources:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart IIII</h4>
                  <p className="text-sm text-gray-600">Compression ignition engines (generators, emergency power)</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart JJJJ</h4>
                  <p className="text-sm text-gray-600">Spark ignition engines for various applications</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart Dc</h4>
                  <p className="text-sm text-gray-600">Steam generating units and boilers</p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Subpart KKKK</h4>
                  <p className="text-sm text-gray-600">Stationary combustion turbines</p>
                </div>
              </div>
            </div>

            {/* NESHAP */}
            <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">40 CFR 63 - NESHAP Requirements</h3>
              <p className="text-gray-600 mb-4">Hazardous Air Pollutant standards for military facilities:</p>
              <div className="space-y-3">
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Subpart ZZZZ</h4>
                    <p className="text-sm text-gray-600">Stationary reciprocating internal combustion engines (RICE)</p>
                  </div>
                </div>
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Subpart JJJJJJ</h4>
                    <p className="text-sm text-gray-600">Industrial, commercial, and institutional boilers</p>
                  </div>
                </div>
                <div className="flex items-start bg-white p-4 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Subpart DDDDD</h4>
                    <p className="text-sm text-gray-600">Major source boilers and process heaters</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Military-Specific Considerations */}
            <div className="bg-white p-8 rounded-lg shadow-lg border-l-4 border-green-700">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Military-Specific Considerations</h3>
              <p className="text-gray-600 mb-4">
                Unique aspects of military installation environmental compliance:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-700 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Emergency power generation and backup systems</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-700 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Heating systems for remote Alaska installations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-700 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Vehicle and equipment maintenance facilities</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-700 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Training range and testing facility emissions</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-700 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Federal facility compliance under Executive Orders</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Benefits of Superior Air Quality</h2>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <p className="text-gray-600 mb-6 leading-relaxed">
                Maintaining superior air quality at military installations provides numerous benefits:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start bg-gray-50 p-4 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-700 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-green-50 p-8 rounded-lg max-w-4xl mx-auto border-2 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center flex items-center justify-center">
              <Shield className="w-8 h-8 text-green-700 mr-3" />
              Our Commitment to Military Excellence
            </h2>
            <p className="text-lg text-gray-700 text-center leading-relaxed">
              Choose Raven Environmental Consulting, LLC to ensure superior air quality at your military installation. We are dedicated to supporting our servicemen and women with the best air quality solutions available, fostering a safer, healthier, and more productive environment.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Support Mission Readiness with Superior Air Quality</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us to learn more about how we can support your installation's environmental management needs.
          </p>
          <Link
            to="/contact/military"
            className="inline-flex items-center px-8 py-4 bg-white text-green-800 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            Submit Military Installation Inquiry
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MilitaryInstallations;
